import { Link } from "react-router-dom";
import { useEffect } from "react";
import Footer from "../components/Footer";
import { ShieldAlert, FileText, ArrowLeft, ExternalLink, CalendarClock } from "lucide-react";
import {
  DISCLAIMER_TITLE,
  DISCLAIMER_INTRO,
  DISCLAIMER_POINTS,
  DISCLAIMER_FOOTER_LINE,
  DISCLAIMER_VERSION,
  DISCLAIMER_LAST_UPDATED,
} from "../lib/disclaimer";

// Effective date of the current disclaimer version. ISO-8601 for JSON-LD.
const EFFECTIVE_DATE_ISO = "2026-05-01";
const EFFECTIVE_DATE_READABLE = "May 1, 2026";

/**
 * Public, no-auth-required rendering of the full Clinical Decision Support
 * disclaimer. Linked from the global footer + BAA + Terms pages so external
 * counsel, insurance carriers, and prospective clinicians can read it without
 * signing up.
 *
 * Source-of-truth constants come from /lib/disclaimer.js — the same module
 * powers the first-login modal, AI-output blocks, and PDF stamping. One bump
 * to DISCLAIMER_VERSION updates every surface at once.
 */
export default function DisclaimerLegal() {
  // Inject JSON-LD structured data into <head> for SEO + rich results.
  // Removed on unmount so navigating away cleans up the tag.
  useEffect(() => {
    const canonicalUrl = `${window.location.origin}/legal/disclaimer`;
    const text =
      `${DISCLAIMER_INTRO}\n\n` +
      DISCLAIMER_POINTS.map((p, i) => `${i + 1}. ${p.title} ${p.body}`).join("\n\n") +
      `\n\n${DISCLAIMER_FOOTER_LINE}`;
    const ld = {
      "@context": "https://schema.org",
      "@type": "DigitalDocument",
      additionalType: "LegalNotice",
      name: DISCLAIMER_TITLE,
      headline: DISCLAIMER_TITLE,
      description:
        "ProsthoAI Clinical Decision Support disclaimer for licensed dental professionals. " +
        "Not FDA cleared. AI outputs require independent clinical verification.",
      identifier: `prosthoai-clinical-disclaimer-${DISCLAIMER_VERSION}`,
      version: DISCLAIMER_VERSION,
      dateModified: EFFECTIVE_DATE_ISO,
      datePublished: EFFECTIVE_DATE_ISO,
      inLanguage: "en-US",
      url: canonicalUrl,
      mainEntityOfPage: canonicalUrl,
      text,
      publisher: {
        "@type": "Organization",
        name: "CamelionAI LLC",
        url: "https://camelionai.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Boca Raton",
          addressRegion: "FL",
          addressCountry: "US",
        },
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Licensed dental professionals — prosthodontists, general dentists, dental specialists",
      },
      about: {
        "@type": "Thing",
        name: "AI-powered dental clinical decision support",
      },
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "prosthoai-disclaimer-jsonld";
    tag.text = JSON.stringify(ld);
    document.head.appendChild(tag);

    // Canonical link + meta description for SEO
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = canonicalUrl;
    canonical.id = "prosthoai-disclaimer-canonical";
    document.head.appendChild(canonical);

    const metaDesc = document.createElement("meta");
    metaDesc.name = "description";
    metaDesc.content =
      "ProsthoAI Clinical Decision Support Disclaimer — for licensed dental professionals. " +
      `Effective ${EFFECTIVE_DATE_READABLE} · Version ${DISCLAIMER_VERSION}. ` +
      "Not FDA cleared. CamelionAI LLC, Boca Raton, Florida.";
    metaDesc.id = "prosthoai-disclaimer-meta-desc";
    document.head.appendChild(metaDesc);

    const prevTitle = document.title;
    document.title = `Clinical Decision Support Disclaimer · ProsthoAI — CamelionAI LLC`;

    return () => {
      document.getElementById("prosthoai-disclaimer-jsonld")?.remove();
      document.getElementById("prosthoai-disclaimer-canonical")?.remove();
      document.getElementById("prosthoai-disclaimer-meta-desc")?.remove();
      document.title = prevTitle;
    };
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--pa-bg)" }}
      data-testid="legal-disclaimer-page"
    >
      <header className="px-6 lg:px-12 py-6 border-b border-[var(--pa-border)] shrink-0">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Link to="/" className="flex items-baseline gap-2" data-testid="legal-disclaimer-brand">
            <span className="font-serif text-2xl text-[var(--pa-text)]">Prostho</span>
            <span className="font-serif text-2xl text-[var(--pa-gold)]">AI</span>
          </Link>
          <Link
            to="/"
            data-testid="legal-disclaimer-back"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
          >
            <ArrowLeft size={12} /> Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert size={14} className="text-[var(--pa-gold)]" />
            <div className="kicker">Legal · Clinical Decision Support</div>
          </div>
          <h1
            className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2 leading-tight"
            data-testid="legal-disclaimer-title"
          >
            {DISCLAIMER_TITLE}
          </h1>

          {/* Effective date / version block — prominent, machine-readable via <time> */}
          <div
            data-testid="legal-disclaimer-effective-date"
            className="mt-5 flex flex-wrap items-center gap-3 p-3 rounded-md border border-[var(--pa-gold)]/30 bg-[var(--pa-surface)]"
          >
            <CalendarClock size={14} className="text-[var(--pa-gold)] shrink-0" />
            <div className="text-xs leading-snug">
              <span className="text-[var(--pa-text)] font-semibold">Effective: </span>
              <time
                dateTime={EFFECTIVE_DATE_ISO}
                className="text-[var(--pa-text-muted)]"
                data-testid="legal-disclaimer-effective-time"
              >
                {EFFECTIVE_DATE_READABLE}
              </time>
              <span className="mx-2 text-[var(--pa-text-dim)]">·</span>
              <span className="text-[var(--pa-text)] font-semibold">Version </span>
              <span className="font-mono text-[var(--pa-gold)]" data-testid="legal-disclaimer-version-pill">
                {DISCLAIMER_VERSION}
              </span>
              <span className="mx-2 text-[var(--pa-text-dim)]">·</span>
              <span className="text-[var(--pa-text-muted)]">Last updated {DISCLAIMER_LAST_UPDATED}</span>
            </div>
          </div>

          <div className="gold-divider my-8" />

          <p className="text-sm text-[var(--pa-text-muted)] leading-relaxed" data-testid="legal-disclaimer-intro">
            {DISCLAIMER_INTRO}
          </p>

          <ol
            data-testid="legal-disclaimer-points"
            className="mt-7 space-y-5 list-decimal list-outside ml-5 text-sm text-[var(--pa-text)]"
          >
            {DISCLAIMER_POINTS.map((p, i) => (
              <li key={i} className="leading-relaxed pl-1">
                <strong className="block text-[var(--pa-gold)] uppercase tracking-wide text-xs mb-1">
                  {p.title.replace(/\.$/, "")}
                </strong>
                <span className="text-[var(--pa-text-muted)]">{p.body}</span>
              </li>
            ))}
          </ol>

          <div className="mt-10 p-4 rounded-md border border-[var(--pa-border)] bg-[var(--pa-surface)] text-xs text-[var(--pa-text-muted)] leading-relaxed">
            {DISCLAIMER_FOOTER_LINE}
          </div>

          {/* Cross-links to related legal docs */}
          <section
            data-testid="legal-disclaimer-related"
            className="mt-10 grid sm:grid-cols-2 gap-3"
          >
            <Link
              to="/baa"
              data-testid="legal-disclaimer-link-baa"
              className="flex items-center justify-between gap-3 p-4 rounded-md border border-[var(--pa-border)] bg-[var(--pa-bg-2)] hover:border-[var(--pa-gold)]/60 transition"
            >
              <div>
                <div className="text-sm text-[var(--pa-text)]">Business Associate Agreement</div>
                <div className="text-[11px] text-[var(--pa-text-dim)] mt-0.5">
                  HIPAA BAA covering PHI handling on ProsthoAI.
                </div>
              </div>
              <FileText size={14} className="text-[var(--pa-gold)] shrink-0" />
            </Link>
            <Link
              to="/terms"
              data-testid="legal-disclaimer-link-terms"
              className="flex items-center justify-between gap-3 p-4 rounded-md border border-[var(--pa-border)] bg-[var(--pa-bg-2)] hover:border-[var(--pa-gold)]/60 transition"
            >
              <div>
                <div className="text-sm text-[var(--pa-text)]">Terms of Service</div>
                <div className="text-[11px] text-[var(--pa-text-dim)] mt-0.5">
                  Platform use, billing, and termination terms.
                </div>
              </div>
              <FileText size={14} className="text-[var(--pa-gold)] shrink-0" />
            </Link>
            <a
              href="https://camelionai.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="legal-disclaimer-link-camelionai"
              className="sm:col-span-2 flex items-center justify-between gap-3 p-4 rounded-md border border-[var(--pa-border)] bg-[var(--pa-bg-2)] hover:border-[var(--pa-gold)]/60 transition"
            >
              <div>
                <div className="text-sm text-[var(--pa-text)]">
                  CamelionAI LLC <span className="text-[10px] text-[var(--pa-text-dim)] ml-2">Boca Raton, Florida</span>
                </div>
                <div className="text-[11px] text-[var(--pa-text-dim)] mt-0.5">
                  Contact, corporate, and patent information.
                </div>
              </div>
              <ExternalLink size={14} className="text-[var(--pa-gold)] shrink-0" />
            </a>
          </section>

          <div className="mt-12 text-[11px] text-[var(--pa-text-dim)] text-center">
            For licensed dental professionals only. By creating a ProsthoAI account you affirm
            you have read and accept this disclaimer.
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
